import {Singleton} from 'annotation';
import environment from 'environment';

import ProxyService from './Proxy';

import EntityAware from '../entity/EntityAware';
import Kegiatan from "../entity/Kegiatan";
import Parameter from "./Parameter";

@Singleton
export default class PresensiKegiatanService extends ProxyService {

    public api: string = environment.PRESENSI_KEGIATAN_API;

    public serializer: EntityAware = Kegiatan;

    public findDaftarKegiatan(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(this.api, param);
        return this.request(parameter);
    }

  /**
   * Download template excel peserta kegiatan berdasarkan filter.
   * Backend endpoint: GET /v1/presensi/kegiatan/template?komplek=..&pengajian=..
   */
  public downloadTemplateKegiatan(komplek: string = '', pengajian: string = '') {
    let api: string = this.api + `/template`;
    const params: string[] = [];
    
    if (komplek) {
      params.push(`komplek=${komplek}`);
    }
    
    if (pengajian) {
      params.push(`pengajian=${pengajian}`);
    }
    
    if (params.length > 0) {
      api += `?${params.join('&')}`;
    }
    
    const fileName = `Template Peserta Kegiatan.xlsx`;

    return super.downloadFile(api, fileName, false);
  }

  public createKegiatan(kegiatan: Kegiatan): Promise<any> {
    const parameter = new Parameter(kegiatan)
      .setMethod(Parameter.methods.POST)
      .setUrl(this.api)
      .serializeData(this.serializer);

    return this.request(parameter);
  }

  /**
   * Update kegiatan
   * PUT /v1/presensi/kegiatan/:id
   */
  public updateKegiatan(kegiatanId: number, kegiatan: Kegiatan): Promise<any> {
    const api: string = this.api + `/${kegiatanId}`;
    const parameter = new Parameter(kegiatan)
      .setMethod(Parameter.methods.PUT)
      .setUrl(api)
      .serializeData(this.serializer);
    return this.request(parameter);
  }

  public uploadKegiatan(kegiatan: Kegiatan): Promise<any> {
    const formData: FormData = new FormData();
    const api: string = this.api + `/upload`;

    if (kegiatan.file) {
      formData.append('file', kegiatan.file);
    }

    // Buat payload sesuai kontrak API
    // Untuk PENGAJIAN: nama, jenis, jenis_kelamin, wilayah, waktu_mulai, waktu_selesai, keterangan, is_active
    // Untuk JAMAAH: nama, jenis, jenis_kelamin, wilayah, keterangan, is_active
    const payload: any = {
      nama: kegiatan.nama,
      jenis: kegiatan.jenis,
      jenis_kelamin: kegiatan.jenis_kelamin,
      wilayah: kegiatan.wilayah,
      keterangan: kegiatan.keterangan,
      is_active: kegiatan.is_active
    };

    // Tambahkan waktu_mulai dan waktu_selesai hanya jika ada (untuk PENGAJIAN)
    if (kegiatan.waktu_mulai) {
      payload.waktu_mulai = kegiatan.waktu_mulai;
    }
    if (kegiatan.waktu_selesai) {
      payload.waktu_selesai = kegiatan.waktu_selesai;
    }

    formData.append('payload', JSON.stringify(payload));

    const parameter: Parameter = new Parameter(formData)
      .setUrl(api)
      .setMethod(Parameter.methods.POST)
      .setEnableNotif(false);

    return this.request(parameter);
  }

  /**
   * Get daftar peserta kegiatan
   * GET /v1/presensi/kegiatan/:id/peserta?page=0&size=10
   */
  public findPesertaKegiatan(kegiatanId: number, param: any = {}): Promise<any> {
    const api: string = this.api + `/${kegiatanId}/peserta`;
    const parameter = new Parameter().setUrl(api, param);
    return this.request(parameter);
  }

  /**
   * Tambah peserta kegiatan
   * POST /v1/presensi/kegiatan/:id/peserta
   * Payload: { nomor: string, pengajian_id?: number }
   */
  public addPesertaKegiatan(kegiatanId: number, payload: { nomor: string; pengajian_id?: number }): Promise<any> {
    const api: string = this.api + `/${kegiatanId}/peserta`;
    const parameter = new Parameter(payload)
      .setMethod(Parameter.methods.POST)
      .setUrl(api);
    return this.request(parameter);
  }

  /**
   * Hapus peserta kegiatan
   * DELETE /v1/presensi/kegiatan/:id/peserta/:nomor
   */
  public deletePesertaKegiatan(kegiatanId: number, nomor: string): Promise<any> {
    const api: string = this.api + `/${kegiatanId}/peserta/${nomor}`;
    const parameter = new Parameter()
      .setMethod(Parameter.methods.DELETE)
      .setUrl(api);
    return this.request(parameter);
  }

  /**
   * Get daftar guru pengajian
   * GET /v1/presensi/guru/pengajian?page=0&size=100
   */
  public findGuruPengajian(): Promise<any> {
    const api: string = environment.PRESENSI_GURU_PENGAJIAN_API;
    const parameter = new Parameter().setUrl(api, {page: 0, size: 100});
    return this.request(parameter);
  }

  /**
   * Pindah peserta dari satu kegiatan ke kegiatan lain
   * POST /v1/presensi/peserta/move
   */
  public movePesertaKegiatan(payload: {
    from_kegiatan_id: number;
    to_kegiatan_id: number;
    nomor: string;
    from_pengajian_id?: number;
    to_pengajian_id?: number;
  }): Promise<any> {
    const api: string = environment.BASE_API + 'v1/presensi/peserta/move';
    const parameter = new Parameter(payload)
      .setMethod(Parameter.methods.POST)
      .setUrl(api);

    return this.request(parameter);
  }

  /**
   * Hapus kegiatan
   * DELETE /v1/presensi/kegiatan/:id
   */
  public deleteKegiatan(kegiatanId: number): Promise<any> {
    const api: string = this.api + `/${kegiatanId}`;
    const parameter = new Parameter()
      .setMethod(Parameter.methods.DELETE)
      .setUrl(api);
    return this.request(parameter);
  }

  /**
   * Update status active/non-active kegiatan
   * PUT /v1/presensi/kegiatan/:id/active
   * Payload: { is_active: true | false }
   */
  public updateActiveStatus(kegiatanId: number, isActive: boolean): Promise<any> {
    const api: string = this.api + `/${kegiatanId}/active`;
    const payload = { is_active: isActive };
    const parameter = new Parameter(payload)
      .setMethod(Parameter.methods.PUT)
      .setUrl(api);
    return this.request(parameter);
  }
}


